<template>
  <div class="blog-card-wrapper">
    <!-- 第一张（index=0）：图片在右 -->
    <!-- 第二张（index=1）：图片在左 -->
    <div
      class="blog-card"
      :class="{ 'image-right': index % 2 === 0, 'image-left': index % 2 !== 0 }"
    >
      <!-- 图片区域 - 可点击 -->
      <div class="blog-card-image-wrapper" @click="handleNavigate">
        <div class="blog-card-image">
          <img :src="post.image" :alt="post.title" />
        </div>
        <!-- 倾斜遮罩 -->
        <div class="skew-mask"></div>
      </div>

      <!-- 内容区域 -->
      <div class="blog-card-content">
        <div class="blog-card-body">
          <h2 class="blog-card-title" @click="handleNavigate">
            {{ post.title }}
          </h2>
          <p class="blog-card-subtitle">{{ post.subtitle }}</p>
          <div class="blog-card-divider"></div>
          <p class="blog-card-excerpt">{{ post.excerpt }}</p>
        </div>

        <div class="blog-card-footer">
          <!-- 文章类别徽章 - 使用可复用组件 -->
          <TopicBadge
            :topic-id="post.topicId"
            :name="post.topic"
            :icon="getCategoryIcon(post.topicId)"
          />

          <a
            :href="post.link"
            @click.prevent="handleNavigate"
            class="read-more-btn"
          >
            <span>Read More</span>
            <svg
              class="arrow-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2.5"
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useRouter, useRoute } from "vue-router";
import { useLoading } from "@/composables/useLoading";
import { getTopicById } from "@/config/topics";
import { saveReferrer } from "@/utils/navigationState";
import TopicBadge from "./TopicBadge.vue";

export default {
  name: "BlogPostCard",
  components: {
    TopicBadge,
  },
  props: {
    post: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      default: 0,
    },
  },
  setup(props) {
    const router = useRouter();
    const route = useRoute();
    const { showLoading, hideLoading } = useLoading();

    const handleNavigate = () => {
      showLoading("加载文章中...");

      // 保存来源页面
      saveReferrer(route.fullPath);

      // 使用 router.push 导航
      router
        .push(props.post.link)
        .then(() => {
          // 导航完成后隐藏加载动画
          setTimeout(() => {
            hideLoading();
          }, 300);
        })
        .catch(() => {
          hideLoading();
        });
    };

    const getCategoryIcon = (topicId) => {
      const topic = getTopicById(topicId);
      return topic ? topic.icon : "📝";
    };

    return {
      handleNavigate,
      getCategoryIcon,
    };
  },
};
</script>

<style scoped>
.blog-card-wrapper {
  width: 100%;
  max-width: 100%;
}

.blog-card {
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
  position: relative;
}

.blog-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

/* 分类标签 */
.category-tag {
  position: absolute;
  top: 16px;
  z-index: 10;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.95),
    rgba(139, 92, 246, 0.95)
  );
  backdrop-filter: blur(10px);
  border-radius: 20px;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  transition: all 0.3s ease;
}

/* 图片在右时，标签在右上角（与图片同侧但在内容区域上方） */
.image-right .category-tag {
  right: 16px;
}

/* 图片在左时，标签在左上角（与图片同侧但在内容区域上方） */
.image-left .category-tag {
  left: 16px;
}

.blog-card:hover .category-tag {
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
}

.category-icon {
  font-size: 14px;
  line-height: 1;
}

.category-name {
  font-size: 12px;
  font-weight: 600;
  color: white;
  letter-spacing: 0.02em;
}

@media (min-width: 640px) {
  .category-tag {
    padding: 7px 16px;
  }

  .category-icon {
    font-size: 15px;
  }

  .category-name {
    font-size: 13px;
  }
}

/* 桌面端布局 - 图片在右 */
@media (min-width: 640px) {
  .blog-card {
    flex-direction: row;
    height: 240px;
  }

  /* 图片在左 */
  .blog-card.image-left {
    flex-direction: row-reverse;
  }
}

@media (min-width: 1024px) {
  .blog-card {
    height: 260px;
  }
}

@media (min-width: 1280px) {
  .blog-card {
    height: 280px;
  }
}

/* 图片容器 */
.blog-card-image-wrapper {
  position: relative;
  width: 100%;
  height: 240px;
  flex-shrink: 0;
  overflow: hidden;
}

@media (min-width: 640px) {
  .blog-card-image-wrapper {
    width: 42%;
    height: 240px;
  }
}

@media (min-width: 1024px) {
  .blog-card-image-wrapper {
    width: 40%;
    height: 260px;
  }
}

@media (min-width: 1280px) {
  .blog-card-image-wrapper {
    width: 38%;
    height: 280px;
  }
}

@media (min-width: 640px) {
  /* 图片在右时，左侧有圆角 */
  .image-right .blog-card-image-wrapper {
    border-radius: 16px 0 0 16px;
  }

  /* 图片在左时，右侧有圆角 */
  .image-left .blog-card-image-wrapper {
    border-radius: 0 16px 16px 0;
  }
}

/* 图片 */
.blog-card-image {
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #f3f4f6;
}

@media (min-width: 640px) {
  /* 图片在右时，左侧有圆角 */
  .image-right .blog-card-image {
    border-radius: 16px 0 0 16px;
  }

  /* 图片在左时，右侧有圆角 */
  .image-left .blog-card-image {
    border-radius: 0 16px 16px 0;
  }
}

.blog-card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.7s ease;
}

.blog-card:hover .blog-card-image img {
  transform: scale(1.1);
}

/* 倾斜遮罩 - 创建倾斜分界线 */
.skew-mask {
  display: none;
}

@media (min-width: 640px) {
  .skew-mask {
    display: block;
    position: absolute;
    top: -5px;
    bottom: -5px;
    width: 80px;
    background: white;
    z-index: 1;
  }

  /* 图片在右时，遮罩从图片左边缘向内容方向延伸 */
  .image-right .skew-mask {
    right: -40px;
    transform: skewX(-6deg);
    transform-origin: left;
  }

  /* 图片在左时，遮罩从图片右边缘向内容方向延伸 */
  .image-left .skew-mask {
    left: -40px;
    transform: skewX(6deg);
    transform-origin: right;
  }
}

/* 内容区域 */
.blog-card-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 24px;
  min-width: 0;
  min-height: 0;
  background: white;
  position: relative;
  z-index: 2;
  overflow: hidden;
}

/* 星空斑点装饰 */
.blog-card-content::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image: radial-gradient(
      circle at 15% 20%,
      rgba(59, 130, 246, 0.04) 0%,
      transparent 3%
    ),
    radial-gradient(
      circle at 85% 30%,
      rgba(139, 92, 246, 0.03) 0%,
      transparent 2%
    ),
    radial-gradient(
      circle at 45% 70%,
      rgba(236, 72, 153, 0.03) 0%,
      transparent 2.5%
    ),
    radial-gradient(
      circle at 70% 15%,
      rgba(59, 130, 246, 0.025) 0%,
      transparent 2%
    ),
    radial-gradient(
      circle at 25% 85%,
      rgba(139, 92, 246, 0.035) 0%,
      transparent 3%
    ),
    radial-gradient(
      circle at 90% 80%,
      rgba(59, 130, 246, 0.03) 0%,
      transparent 2%
    ),
    radial-gradient(
      circle at 10% 50%,
      rgba(236, 72, 153, 0.025) 0%,
      transparent 2%
    ),
    radial-gradient(
      circle at 60% 45%,
      rgba(139, 92, 246, 0.02) 0%,
      transparent 1.5%
    );
  pointer-events: none;
  opacity: 0.8;
  z-index: 0;
}

.blog-card-body,
.blog-card-footer {
  position: relative;
  z-index: 1;
}

@media (min-width: 640px) {
  .blog-card-content {
    padding: 28px 32px;
  }

  /* 图片在右时，内容向右延伸覆盖图片 */
  .image-right .blog-card-content {
    margin-right: -30px;
    padding-right: 50px;
  }

  /* 图片在左时，内容向左延伸覆盖图片 */
  .image-left .blog-card-content {
    margin-left: -30px;
    padding-left: 50px;
  }
}

@media (min-width: 1024px) {
  .blog-card-content {
    padding: 32px 40px;
  }

  .image-right .blog-card-content {
    padding-right: 60px;
  }

  .image-left .blog-card-content {
    padding-left: 60px;
  }
}

.blog-card-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/* 标题 */
.blog-card-title {
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.3;
  margin-bottom: 10px;
  transition: color 0.3s ease;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.blog-card-title:hover {
  color: #2563eb;
}

/* 图片容器可点击 */
.blog-card-image-wrapper {
  cursor: pointer;
}

@media (min-width: 640px) {
  .blog-card-title {
    font-size: 28px;
    margin-bottom: 12px;
  }
}

@media (min-width: 1024px) {
  .blog-card-title {
    font-size: 32px;
    margin-bottom: 14px;
  }
}

@media (min-width: 1280px) {
  .blog-card-title {
    font-size: 36px;
  }
}

/* 副标题 */
.blog-card-subtitle {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 0;
}

@media (min-width: 640px) {
  .blog-card-subtitle {
    font-size: 13px;
    margin-bottom: 14px;
  }
}

/* 装饰线 */
.blog-card-divider {
  width: 64px;
  height: 4px;
  background: linear-gradient(to right, #3b82f6, #8b5cf6, #ec4899);
  border-radius: 2px;
  margin-bottom: 12px;
  flex-shrink: 0;
}

@media (min-width: 640px) {
  .blog-card-divider {
    margin-bottom: 14px;
  }
}

/* 摘要 - 单行显示，超出用省略号 */
.blog-card-excerpt {
  font-size: 14px;
  line-height: 1.6;
  color: #4b5563;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 0;
}

@media (min-width: 640px) {
  .blog-card-excerpt {
    font-size: 14px;
  }
}

@media (min-width: 1024px) {
  .blog-card-excerpt {
    font-size: 15px;
  }
}

/* 底部信息栏 */
.blog-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
  gap: 12px;
  flex-shrink: 0;
}

@media (min-width: 640px) {
  .blog-card-footer {
    margin-top: 18px;
    padding-top: 18px;
  }
}

/* Read More按钮 */
.read-more-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  font-size: 13px;
  font-weight: 700;
  color: white;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
  background-size: 200% 200%;
  border-radius: 9999px;
  text-decoration: none;
  transition: all 0.3s ease;
  animation: gradient-shift 3s ease infinite;
  flex-shrink: 0;
  white-space: nowrap;
}

.read-more-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.4);
  animation: gradient-shift 1s ease infinite;
}

.read-more-btn span {
  transition: transform 0.3s ease;
}

.read-more-btn:hover span {
  transform: translateX(-4px);
}

.arrow-icon {
  width: 18px;
  height: 18px;
  transition: transform 0.3s ease;
}

.read-more-btn:hover .arrow-icon {
  transform: translateX(4px) rotate(-10deg);
}

@keyframes gradient-shift {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}
</style>
